import { useState } from "react";
import { CheckCircle2, XCircle, MapPin } from "lucide-react";

export function QuizStep({ situation, review, savedAnswers, onDone }) {
  const qs = situation.questions;
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(savedAnswers || []);
  const [picked, setPicked] = useState(savedAnswers ? savedAnswers[0] : null);
  const [checked, setChecked] = useState(!!review);
  const q = qs[idx];

  const validate = () => {
    setAnswers([...answers.slice(0, idx), picked]);
    setChecked(true);
  };

  const next = () => {
    if (idx + 1 >= qs.length) return onDone(answers);
    setIdx(idx + 1);
    setPicked(review ? savedAnswers?.[idx + 1] ?? null : null);
    setChecked(!!review);
  };

  const optionCls = (i) => {
    if (!checked) return picked === i ? "border-orange-500 bg-orange-50" : "border-slate-200 bg-white hover:border-slate-400";
    if (i === q.answer_index) return "border-emerald-500 bg-emerald-50";
    if (i === picked) return "border-red-500 bg-red-50";
    return "border-slate-200 bg-white opacity-60";
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-slate-900 text-white p-5" data-testid="lesson-situation-context">
        <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase mb-2">
          <MapPin className="w-4 h-4" /> Sur le chantier
        </div>
        <p className="text-base">{situation.context}</p>
      </div>
      <div className="flex gap-1.5" data-testid="lesson-quiz-progress">
        {qs.map((item, i) => (
          <div key={item.question} className={`h-2 flex-1 rounded-full ${i < idx ? "bg-orange-500" : i === idx ? "bg-amber-400" : "bg-slate-300"}`} />
        ))}
      </div>
      <h3 className="font-display text-2xl font-bold" data-testid="lesson-quiz-question">{q.question}</h3>
      <div className="space-y-3">
        {q.options.map((o, i) => (
          <button
            key={o}
            disabled={checked}
            onClick={() => setPicked(i)}
            data-testid={`lesson-quiz-option-${i}`}
            className={`w-full min-h-14 text-left rounded-xl border-2 px-4 py-3 font-medium flex items-center gap-3 transition-colors ${optionCls(i)}`}
          >
            <span className="shrink-0 w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center font-bold text-sm">
              {String.fromCharCode(65 + i)}
            </span>
            <span className="flex-1">{o}</span>
            {checked && i === q.answer_index && <CheckCircle2 className="w-6 h-6 text-emerald-600" />}
            {checked && i === picked && i !== q.answer_index && <XCircle className="w-6 h-6 text-red-600" />}
          </button>
        ))}
      </div>
      {checked && q.explanation && (
        <div
          data-testid="lesson-quiz-feedback"
          className={`rounded-xl p-4 border-2 rise ${picked === q.answer_index ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-red-50 border-red-300 text-red-900"}`}
        >
          <p className="font-bold">{picked === q.answer_index ? "Bien joué !" : "Pas tout à fait."}</p>
          <p className="text-sm mt-1">{q.explanation}</p>
        </div>
      )}
      {!checked ? (
        <button
          disabled={picked === null}
          onClick={validate}
          data-testid="lesson-quiz-submit-btn"
          className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-display text-xl font-bold uppercase transition-colors disabled:opacity-40"
        >
          Valider
        </button>
      ) : (
        <button
          onClick={next}
          data-testid="lesson-quiz-next-btn"
          className="w-full h-14 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-display text-xl font-bold uppercase transition-colors"
        >
          {idx + 1 >= qs.length ? (review ? "Terminer" : "Voir mon résultat") : "Question suivante"}
        </button>
      )}
    </div>
  );
}
